// owner/alerts/deviceAlerts.js — how THIS device gets alerts, behind one interface (the utils/sessionStore pattern).
//
// The owner pages are shared by the web and the store app (§10 step 8, option C), but the two get alerts in different
// ways: a computer's browser through web push, whose helpers keep a device id in browser storage
// (utils/serviceWorker.js), and the app through Firebase. Neither may leak into the other's build, so the pages
// talk to this seam only, and each entry point installs its own channel:
//   - the web entry (src/index.js) installs owner/web/browserAlerts.js;
//   - the app entry (owner/app/index.js) installs owner/app/alerts/appAlerts.js.
// Nothing installed (a test, a staff-only page) means "no alerts on this device" and the pages show none.
// Plan: tasks/owner-mobile-app-build.md §10 "8d".
//
// A channel:
//   kind        'none' | 'browser' (a computer) | 'phoneBrowser' (the web on a phone: the app is offered instead,
//               Adil 5 Nov) | 'app'
//   getState()  { status, storePlatform, reason } — status: 'unsupported' | 'unavailable' (switched off on the
//               server) | 'prompt' (never asked) | 'off' | 'on' | 'denied' | 'checking' | 'error' (could not read;
//               the page offers a retry)
//   subscribe(listener) → unsubscribe, called on every state change
//   refresh()   re-reads the state (the settings page opening, the app returning)
//   enable()    asks the device's permission if needed, then registers → { ok, reason? }
//   disable()   stops alerts on this device (the browser; the app removes its device at sign-out)

import { useEffect, useSyncExternalStore } from 'react';

const NONE_STATE = Object.freeze({ status: 'unsupported', storePlatform: null });

export const NO_CHANNEL = Object.freeze({
  kind: 'none',
  getState: () => NONE_STATE,
  subscribe: () => () => {},
  refresh: async () => {},
  enable: async () => ({ ok: false, reason: 'unsupported' }),
  disable: async () => {}
});

let channel = NO_CHANNEL;

/** Called once by an entry point, before the first render. */
export const installDeviceAlerts = (next) => { channel = next || NO_CHANNEL; };

export const deviceAlertsChannel = () => channel;

/** A small store for a channel's state: the one place a channel announces a change. */
export const createStateStore = (initial) => {
  let state = initial;
  const listeners = new Set();
  return {
    get: () => state,
    set: (patch) => {
      state = { ...state, ...patch };
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
};

/**
 * This device's alerts for a page: the channel, its live state, and its actions. `refreshOnMount` re-reads the state
 * when the page opens (settings), since the owner may have changed the permission in the browser or phone meanwhile.
 */
export const useDeviceAlerts = ({ refreshOnMount = false } = {}) => {
  const active = channel;
  const state = useSyncExternalStore(active.subscribe, active.getState, active.getState);
  useEffect(() => {
    if (refreshOnMount) active.refresh().catch(() => {});
  }, [active, refreshOnMount]);
  return { kind: active.kind, ...state, enable: active.enable, disable: active.disable, refresh: active.refresh };
};
